import { useFinanceIntegrity, type IntegrityGroup, type StreamFinanceSummary } from "@/lib/finance-integrity";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { formatVnd } from "@/lib/finance";
import { fmtDateTime } from "@/lib/utils";

const GROUP_LABEL: Record<string, string> = {
  amount_mismatch: "Sai số tiền (qty × unit_price ≠ amount)",
  invalid_date: "Ngày không hợp lệ",
  invalid_kind: "Sai loại / danh mục / dòng",
  bad_amount: "Số tiền không hợp lệ",
  duplicate_risk: "Khả năng trùng",
};

function MoneyLine({ label, value, strong=false }: { label:string; value:number; strong?:boolean }) {
  return <div className="flex justify-between gap-3 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <span className={strong ? "font-semibold font-mono" : "font-mono"}>{formatVnd(Number(value || 0))}</span>
  </div>;
}

function StreamCard({ s }: { s: StreamFinanceSummary }) {
  return <Card className="p-3 space-y-2">
    <div className="flex items-center gap-2">
      <div className="text-sm font-semibold">{s.label}</div>
      <Badge variant="outline" className="text-[10px]">{s.finance_rows} dòng sổ</Badge>
    </div>
    <MoneyLine label="Tổng thu" value={s.income} />
    <MoneyLine label="Tổng chi" value={s.expense} />
    <MoneyLine label="Số dư theo sổ" value={s.ledger_balance} strong />
    <MoneyLine label="Còn phải thu" value={s.receivable_total} strong />
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-muted-foreground">Chênh lệch đối soát</span>
      <span className="font-mono">{s.reconcile_diff == null ? "Chưa có dữ liệu Cake" : formatVnd(Number(s.reconcile_diff || 0))}</span>
    </div>
    <div className="text-[11px] text-muted-foreground">
      Còn phải thu lấy đúng công thức Công nợ: task chưa tất toán, chưa thu đủ, total_amount - deposit_amount. Không cộng vào số dư theo sổ.
    </div>
  </Card>;
}

export function FinanceIntegrityPanel() {
  const { data, isLoading, refetch, isFetching } = useFinanceIntegrity();
  const ordered = ["amount_mismatch","invalid_date","invalid_kind","bad_amount","duplicate_risk"];

  return <div className="space-y-3">
    <div className="flex items-center gap-2">
      <h2 className="text-base font-semibold">Kiểm tra tính toàn vẹn finance_entries</h2>
      <div className="flex-1" />
      <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isFetching ? "animate-spin" : ""}`} /> Quét lại
      </Button>
    </div>

    {isLoading || !data ? <div className="text-sm text-muted-foreground py-6 text-center">Đang quét…</div> : <>
      <Card className={`p-4 flex items-center gap-3 ${data.clean ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
        {data.clean ? <CheckCircle2 className="h-8 w-8 text-emerald-500" /> : <AlertTriangle className="h-7 w-7 text-amber-500" />}
        <div className="text-sm">
          <div className={data.clean ? "font-semibold text-emerald-500" : "font-semibold"}>
            bad_rows = {data.bad_rows ?? data.bad_total ?? 0} {data.clean ? "— Finance data is clean." : "— cần sửa"}
          </div>
          <div className="text-xs text-muted-foreground">
            Đã kiểm {data.total} dòng. Warning rows = {data.warning_total ?? 0}. Warning không làm mất trạng thái clean.
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StreamCard s={data.streams.main} />
        <StreamCard s={data.streams.side} />
      </div>

      {data.unassigned_statement && data.unassigned_statement.statement_rows > 0 && (
        <Card className="p-3 border-amber-500/30 bg-amber-500/5">
          <div className="text-sm font-medium">Cake chưa phân dòng: {data.unassigned_statement.statement_rows} dòng</div>
          <div className="text-xs text-muted-foreground">Các dòng statement chưa khớp finance không được cộng vào Dòng chính/Dòng phụ.</div>
          <div className="mt-1 text-xs font-mono">Số dư chưa phân dòng: {formatVnd(data.unassigned_statement.balance)}</div>
        </Card>
      )}
    </>}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {data && ordered.filter(k => data.groups[k]).map(k => {
        const g = data.groups[k] as IntegrityGroup;
        const warn = g.severity === "warning";
        return <Card key={k} className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-medium">{GROUP_LABEL[k] ?? k}</div>
            <Badge variant={g.count === 0 ? "outline" : warn ? "secondary" : "destructive"} className="text-[10px]">
              {warn ? "warning" : "bad"}: {g.count}
            </Badge>
          </div>
          {g.count === 0 ? <div className="text-xs text-muted-foreground">OK — không có {warn ? "cảnh báo" : "lỗi"}.</div> : (
            <div className="max-h-56 overflow-auto text-xs space-y-1">
              {g.rows.slice(0,20).map((r,i) => (
                <div key={r.id ?? r.sample_id ?? i} className="border border-border/40 rounded px-2 py-1 font-mono">
                  <div className="flex justify-between gap-2">
                    <span className="truncate">{r.occurred_on ?? "—"} · {r.kind ?? ""} · {r.category ?? ""} · {r.revenue_stream === "main" ? "Dòng chính" : "Dòng phụ"}</span>
                    <span className="shrink-0">{r.amount != null ? formatVnd(Number(r.amount)) : ""}</span>
                  </div>
                  {(r.item_name || r.note) && <div className="text-muted-foreground truncate">{r.item_name ?? ""} {r.note ? `· ${r.note}` : ""}</div>}
                  {r.quantity != null && r.unit_price != null && <div className="text-amber-500">qty {r.quantity} × {Number(r.unit_price).toLocaleString("vi-VN")}</div>}
                  {r.cnt != null && <div className="text-amber-500">trùng {r.cnt} dòng</div>}
                </div>
              ))}
            </div>
          )}
        </Card>;
      })}
    </div>

    <Card className="p-3">
      <div className="text-sm font-medium mb-2">Lịch sử thao tác finance gần đây</div>
      {data && data.audit_recent.length === 0 ? <div className="text-xs text-muted-foreground">Chưa có entry nào trong audit_log cho finance_entries.</div> : (
        <div className="max-h-60 overflow-auto text-xs divide-y divide-border/40">
          {data?.audit_recent.map(a => <div key={a.id} className="py-1.5 flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{a.action}</Badge>
            <span className="truncate flex-1">{a.summary ?? a.entity_id}</span>
            <span className="text-muted-foreground">{fmtDateTime(new Date(a.created_at))}</span>
            {a.undone_at && <Badge variant="outline" className="text-[10px] text-orange-400">đã hoàn tác</Badge>}
          </div>)}
        </div>
      )}
    </Card>
  </div>;
}
